import type { NivelServico, SituacaoCaso, SituacaoContrato } from './api.ts';

/**
 * Os nomes que aparecem na tela, num lugar só.
 *
 * As cores dos níveis são as três reservadas em `tokens.css` — `docs/MARCA.md`
 * §4 diz que elas pertencem ao nível de um caso e a mais nada.
 */
export const NIVEIS: Record<NivelServico, { curto: string; longo: string; verbo: string }> = {
  n1_insight: { curto: 'N1', longo: 'N1 · INSIGHT', verbo: 'Descubra' },
  n2_forensic: { curto: 'N2', longo: 'N2 · FORENSIC', verbo: 'Compreenda' },
  n3_kairos: { curto: 'N3', longo: 'N3 · KAIRÓS', verbo: 'Decida' },
  customizado: { curto: 'CUST', longo: 'KAIRÓS Customizado', verbo: 'Decida' },
};

/** A escada do Richard, 14/08/2026. `suspenso` fica fora: é desvio, não etapa. */
export const FILA: SituacaoCaso[] = [
  'contratado',
  'briefing',
  'levantamento',
  'analise',
  'relatorio',
  'entregue',
  'encerrado',
];

export const SITUACOES: Record<SituacaoCaso, string> = {
  contratado: 'Contratado',
  briefing: 'Briefing',
  levantamento: 'Levantamento',
  analise: 'Análise',
  relatorio: 'Relatório',
  entregue: 'Entregue',
  encerrado: 'Encerrado',
  suspenso: 'Suspenso',
};

/**
 * O que cada etapa do caso SIGNIFICA — definições aprovadas pelo Richard em
 * 14/09/2026 (`docs/PRD.md` §42). A escada é de 14/08/2026; o sentido de cada
 * degrau nunca tinha sido escrito, e etapa sem definição é etapa que cada um
 * move quando acha.
 *
 * ⚠️ Aparecem ao passar o mouse no seletor e nos pontos da escada do caso.
 */
export const SIGNIFICADO_DAS_SITUACOES: Record<SituacaoCaso, string> = {
  contratado:
    'Contrato assinado (ou cortesia fechada). Aguarda as informações preliminares do cliente: partes, dívida, processo e datas. O prazo corre da assinatura.',
  briefing:
    'Reunião ou documento com o cliente para apresentar e fechar a lista de partes investigadas dentro do escopo contratado.',
  levantamento:
    'Coleta: consultas, certidões, matrículas e pesquisas. Um documento pode revelar um investigado novo, e o levantamento continua.',
  analise:
    'Cruzamento do que se achou: cadeia societária, linha do tempo, cobertura do crédito e, no Kairós, hipóteses jurídicas.',
  relatorio: 'Redação e conferência do documento.',
  entregue:
    'Documento enviado ao cliente. Mover para cá entrega e trava o relatório. O caso continua vivo para as reuniões de alinhamento e o suporte.',
  encerrado: 'Nada mais a fazer. O Hub grava a data de fim.',
  suspenso: 'Parado por motivo externo — cliente, pagamento. Sai da fila sem sumir.',
};

/** Máscara de CNPJ/CPF enquanto se digita. Guardamos só dígitos. */
export function mascararDocumento(entrada: string, tipo: 'pf' | 'pj'): string {
  const d = entrada.replace(/\D/g, '').slice(0, tipo === 'pf' ? 11 : 14);
  if (tipo === 'pf') {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/**
 * Quantos dias faltam para uma data — em São Paulo, não no relógio de quem
 * abriu a tela. `hoje` vem do servidor.
 */
export function diasAte(data: string, hoje: string): number {
  const a = new Date(`${data}T00:00:00Z`).getTime();
  const b = new Date(`${hoje}T00:00:00Z`).getTime();
  return Math.round((a - b) / 86400000);
}

export function textoDoPrazo(dias: number): string {
  if (dias < 0) return `${Math.abs(dias)} dia${Math.abs(dias) === 1 ? '' : 's'} atrasado`;
  if (dias === 0) return 'vence hoje';
  if (dias === 1) return 'vence amanhã';
  return `faltam ${dias} dias`;
}


/**
 * `12345678920258260100` → `1234567-89.2025.8.26.0100`.
 *
 * ⚠️ **Mora AQUI desde 20/08/2026, e não dentro de uma tela.** Estava em
 * `Investigados.tsx`; quando o card dos marcos da dívida precisou do mesmo
 * formato, copiar teria criado duas versões da mesma máscara — e uma delas
 * ficaria para trás no primeiro ajuste.
 *
 * ⚠️ **Devolve o texto como veio quando não são 20 dígitos.** Número
 * incompleto formatado à força vira um número diferente do que a pessoa
 * digitou, e ela não tem como saber que o Hub o alterou.
 */
export function formatarProcesso(n: string): string {
  const d = n.replace(/\D/g, '');
  if (d.length !== 20) return n;
  return `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9, 13)}.${d.slice(13, 14)}.${d.slice(14, 16)}.${d.slice(16)}`;
}

/**
 * A escada comercial inteira — decisão do Richard, 15/08/2026.
 *
 * ⚠️ **Recusada e Cancelado dizem coisas diferentes.** Recusada: o cliente não
 * aceitou, nunca foi receita. Cancelado: estava fechado e se desfez. Numa
 * carteira, confundir as duas apaga a diferença entre "não vendemos" e
 * "vendemos e perdemos".
 *
 * Mora aqui desde 18/09/2026: a ficha e o formulário do caso dizem a mesma coisa.
 */
export const SITUACOES_DO_CONTRATO: Record<SituacaoContrato, string> = {
  proposta: 'Proposta',
  ativo: 'Ativo',
  concluido: 'Concluído',
  recusada: 'Recusada',
  cancelado: 'Cancelado',
};
